/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, Container} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import {useLocation} from 'react-router-dom'
import Seo from '@salesforce/retail-react-app/app/components/seo'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {
    API_ERROR_MESSAGE,
    INVALID_TOKEN_ERROR,
    INVALID_TOKEN_ERROR_MESSAGE
} from '@salesforce/retail-react-app/app/constants'
import {registerModalContent} from '../registration/index.style'
import ResetPasswordLandingForm from '../../components/reset-password-landing'
import {usePasswordResetAction} from '../../hooks/use-password-reset-action'
import useConfirmationMail from '../../hooks/useConfirmationMail'
import {useAccessToken} from '@salesforce/commerce-sdk-react'

const ResetPasswordLanding = () => {
    const {formatMessage} = useIntl()
    const form = useForm()
    const navigate = useNavigation()
    const {search} = useLocation()
    const queryParams = new URLSearchParams(search)
    const [email, setEmail] = useState('')
    const [token, setToken] = useState('')
    const [isDataReady, setIsDataReady] = useState(false)
    const {resetPasswordAction} = usePasswordResetAction()
    const {token: scapiAccessToken} = useAccessToken()
    const {sendConfirmationMail} = useConfirmationMail()

    useEffect(() => {
        const initializeData = () => {
            setTimeout(() => {
                const encodedEmail = queryParams.get('email') || ''
                const urlEmail = encodedEmail ? atob(decodeURIComponent(encodedEmail)) : ''
                const urlToken = decodeURIComponent(queryParams.get('Token') || '')

                setEmail(urlEmail)
                setToken(urlToken)

                // Set email value in form
                if (urlEmail) {
                    form.setValue('email', urlEmail)
                }

                setIsDataReady(true)
            }, 100)
        }

        initializeData()
    }, [search, form])

    const handleSave = async (data) => {
        if (!isDataReady || !token) {
            form.setError('global', {
                type: 'manual',
                message: formatMessage({
                    defaultMessage: 'Missing required information. Please try again.',
                    id: 'reset_password_landing.error.missing_data'
                })
            })
            return
        }

        form.clearErrors()
        try {
            await resetPasswordAction({
                email: email,
                newPassword: data.password,
                resetToken: token
            })
            if (typeof window !== 'undefined') {
                localStorage.removeItem('passwordResetEmail')
            }
        } catch (error) {
            navigate('/reset-password')
            console.error('Password reset form error:', error)
            let errorData = null
            try {
                if (error.response) {
                    errorData = await error.response.json()
                }
            } catch (err) {
                console.error('Error parsing response:', err)
            }
            const message = INVALID_TOKEN_ERROR.test(errorData?.message || '')
                ? formatMessage(INVALID_TOKEN_ERROR_MESSAGE)
                : formatMessage(API_ERROR_MESSAGE)
            form.setError('global', {type: 'manual', message})
            return
        }

        try {
            await sendConfirmationMail(scapiAccessToken, email)
        } catch (confirmationError) {
            console.error('Failed to send confirmation mail:', confirmationError)
        }
        navigate('/login')
    }

    const handleCancel = () => {
        navigate('/')
    }

    return (
        <Box data-testid="setpassword-page" bg="gray.50" py={[8, 16]}>
            <Seo title="Reset Password" description="Reset your password" />
            <Container bg="white" sx={registerModalContent}>
                <ResetPasswordLandingForm
                    form={form}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    hasToken={isDataReady && !!token}
                    email={email}
                />
            </Container>
        </Box>
    )
}

ResetPasswordLanding.getTemplateName = () => 'setpassword'

ResetPasswordLanding.propTypes = {
    token: PropTypes.string
}

export default ResetPasswordLanding
